<script setup lang="ts" generic="T extends UiItem">
import type { Props as ImgProps } from 'nuxt-base/components/BaseImage.vue'
import { NuxtLink } from '#components'

export type ProductActiveRef = { productId: string; slug: string }
export type OnActiveItem = (args: ProductActiveRef) => void
export type UiItem = {
  key: string
  title: string | None
  productId: string | None
  slug: string | None
  isCurrent: boolean
  name: string
  color: string
  img: ImgProps['img'] | None
  isImage: boolean
  isColor: boolean
  inStock: boolean
}

const t2 = useT2({})
const links = useLinks()

const props = defineProps<{
  items: T[]
  onActive: OnActiveItem | None
  mouseenter(item: T): void
  mouseleave(): void
}>()

const items2 = computed(() => {
  const { items, onActive } = props

  return items.map((item) => {
    const toUI = () => {
      if (onActive) {
        return {
          disabled: false,
          props: undefined,
          onClick: () => {
            const productId = item?.productId
            const slug = item?.slug
            if (productId && slug) onActive({ productId, slug })
          },
        }
      }

      const to = item?.slug ? links.linkProduct({ ...item, slug: item?.slug }) : undefined
      return {
        onClick: undefined,
        disabled: !to,
        props: { disabled: item.isCurrent || !to, to, replace: true },
      }
    }

    return { ui: toUI(), item }
  })
})

const all = computed(() => {
  let color = false
  let img = false
  let txt = false
  for (const item of items2.value) {
    if (item.item.img) img = true
    if (item.item.isColor) color = true
    if (item.item.name) txt = true
  }
  return img ? { img } : color ? { color } : { txt }
})
</script>

<template>
  <div class="_row _wrap _stretch" :class="[all.txt && '_pt08']" @mouseleave="() => mouseleave()">
    <component
      :is="onActive ? 'button' : NuxtLink"
      v-for="{ item, ui } in items2"
      :key="item.key"
      :class="[
        $style.link,
        item.isCurrent ? $style.on : $style.next,
        item.isImage ? $style.img : item.isColor ? '_mb06 _pt04 _pl04 _pr04 _pb08' : '_hm44 _wmin50 _pt02 _pl02 _pr02 _pb02',
        item.inStock ? '_c1' : '_c4',
        ui.disabled && $style.disabled,
      ]"
      :type="onActive ? 'button' : undefined"
      v-bind="ui.props"
      :aria-label="item.title"
      @mouseenter.passive="mouseenter(item)"
      @click="ui.onClick"
    >
      <BaseImage v-if="item.img" class="_w80 _br9 _h80" :class="ui.disabled && $style.disabled" :img="item.img" sizes="120px" lazy ratio="square" />

      <span v-else-if="item.isColor" :style="{ background: item.color }" class="_h80 _w80 _br9" :class="[!item.color && $style.colorOff, ui.disabled && $style.disabled]" />

      <template v-if="item.name">
        <span v-if="item.isImage || item.isColor" :class="[!item.inStock && $style.off, !item.img && !item.isColor ? $style.empty : $style.label]">{{ item.name }}</span>
        <span v-else :class="item.isCurrent ? (item.inStock ? $style.t1 : $style.t2) : item.inStock ? $style.t3 : $style.t4">{{ item.name }}</span>
      </template>

      <span v-if="!item.name && !item.img && !item.isColor" class="_bg5 _br14 _ctr _w100p _h100p">{{ t2('Product') }}</span>
    </component>
  </div>
</template>

<style module>
.link {
  composes: global(_relative);
  composes: global(_col);
  composes: global(_ctrX);
  composes: global(_fw-l);
  composes: global(d_fs09);
  composes: global(m_fs08);
  composes: global(_lh12);

  composes: global(_pt04);
  composes: global(_pr02);
  composes: global(_pb04);
  composes: global(_pl02);

  composes: global(_txt_ctr);

  outline: none;
  border: 0;
  z-index: 1;
  cursor: pointer;
}

.next {
  --bb: var(--blue3);
}
.on {
  --bb: var(--black);
}
.link:global(._c4) {
  --bb: var(--black-3);
}

.link::before {
  content: '';
  display: block;
  left: 0;
  width: 14px;
  height: 3px;
  right: 0;
  bottom: -1px;
  background: var(--bb);
  margin: auto;
  position: absolute;
  z-index: 2;
  opacity: 0.01;
  transform: translateY(7px);
  transition:
    opacity 120ms ease-in,
    transform 120ms ease-in;
}

.on::before {
  transform: translateY(-2px);
  opacity: 0.99;
}

.next:hover::before {
  opacity: 0.99;
  transform: translateY(0);
}

.img {
  composes: global(_hm44);
  /* max-width: 90px; */
  border: 1px solid transparent;
}

.disabled {
  cursor: not-allowed;
  color: hsl(0deg 0% 77%);
}

.next.img:hover {
  color: var(--black);
  text-decoration-color: var(--black);
  border-color: var(--blue3);
}

.label {
  composes: global(_mt06);
  composes: global(_pb04);
  composes: global(_fs08);
  composes: global(_wm100p);
  composes: global(_hidden);

  z-index: 1;
}
.through {
  text-decoration: line-through;
  background: var(--black-6);
}
.off {
  composes: through;
  font-weight: var(--fw-l);
  background: var(--black-6);
  border-color: var(--black-6);
  color: hsl(240, 12%, 36%);
}

.empty {
  composes: global(_ctr);

  composes: global(_h100p);
  composes: global(_br9);
  composes: global(_row);
  composes: global(_row);
  composes: global(_pl02);
  composes: global(_pr02);
  composes: global(_b5);
  composes: global(_wmin60);
  composes: global(_pl2);
  composes: global(_pr2);
}

.t0 {
  composes: global(_ctr);
  composes: global(m_pt1);
  composes: global(m_pb1);
  composes: global(m_pl08);
  composes: global(m_pr08);
  composes: global(d_pl1);
  composes: global(d_pr1);

  composes: global(_wmin100);
  composes: global(_hm50);

  composes: global(_br14);
  composes: global(_b5);

  composes: global(_mta);
  composes: global(_mba);
  composes: global(_mla);
  composes: global(_mra);

  composes: global(_relative);
  composes: global(_hidden);

  z-index: 1;
  --r: 9px;
}

.next:hover .t0 {
  border-color: var(--bb);
  color: var(--bg);
}

.t0::after {
  content: '';
  position: absolute;
  inset: -1px;
  border-radius: var(--r);
  clip-path: inset(calc(100% - 7px) 7px 7px 7px round var(--r));
  transition: clip-path 0.45s cubic-bezier(0.23, 1, 0.32, 1);
  background: var(--bb);
  z-index: -1;
}

.next:hover .t0::after,
.on .t0::after {
  --r: 0px;
  clip-path: inset(0 0 0 0 round var(--r));
}

.t1 {
  /* current, in stock */
  composes: t0;
  composes: global(_fw-xl);
  composes: global(_ls-xs);

  text-decoration-color: var(--blue2);

  composes: global(_c0);
  border-color: var(--bb);
}

.t2 {
  /* current, out of stock */
  composes: t0;
  color: var(--black-5);
  border-color: var(--bb);
  composes: through;
}

.t3 {
  /* next, in stock */
  composes: t0;
  composes: global(_bg0);
}

.t4 {
  /* next, out in stock */
  composes: t0;
  composes: global(off);
  composes: through;
}

.colorOff {
  border: 1px solid var(--black-4);
}
.disabled {
  opacity: 0.3;
}
.on {
  cursor: default;
}
</style>
